import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { NIVEIS } from "../../config/niveis";
import { uploadToStorage } from "../../utils/storageUpload";
import "./KioskLanding.css";

const LogoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const TEMAS = [
  { id: "tema-a", nome: "Escuro Azul", dark: true },
  { id: "tema-b", nome: "Claro", dark: false },
  { id: "tema-c", nome: "Industrial Escuro", dark: true },
  { id: "tema-d", nome: "Industrial Claro", dark: false },
];

export default function KioskLanding() {
  const navigate = useNavigate();
  const { tema, alterarTema } = useTheme();
  const { usuario, logout } = useAuth();
  const { empresaAtual, recarregarEmpresa } = useEmpresa();
  const fileInputRef = useRef(null);

  const [logoUrl, setLogoUrl] = useState(empresaAtual?.logoUrl || null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [showThemeSelector, setShowThemeSelector] = useState(false);

  const empresaNome = empresaAtual?.nome || usuario?.empresa || "Empresa";
  // Para Kiosk (e casos de fallback), usa também usuario.empresaId.
  const empresaIdResolved = empresaAtual?.id || usuario?.empresaId || null;

  // Quem pode editar a UI do logo:
  // - Admin/Gestor
  // - Kiosk (mas só consegue salvar na própria empresa)
  const nivel = Number(usuario?.nivel ?? 0);
  const canEditLogo = nivel >= NIVEIS.ADMIN || nivel === NIVEIS.KIOSK;
  const grupoId = usuario?.grupoId || null;

  // Quem pode realmente enviar/salvar o logo.
  const canUploadLogo =
    canEditLogo &&
    !!grupoId &&
    !!empresaIdResolved &&
    (nivel !== NIVEIS.KIOSK || !usuario?.empresaId || usuario.empresaId === empresaIdResolved);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error("Erro ao sair:", err);
    }
  };

  useEffect(() => {
    setLogoUrl(empresaAtual?.logoUrl || null);
  }, [empresaAtual?.logoUrl]);

  const handleGoToAbrirOs = () => {
    // Kiosk: evita voltar para a tela de configuração usando o botão voltar do tablet
    // (remove a Landing do histórico)
    navigate("/ordens-servico/abrir-nova-os", { replace: true });
  };

  const handleSelectTheme = (temaId) => {
    alterarTema(temaId);
    setShowThemeSelector(false);
  };

  const handleLogoClick = () => {
    if (!canUploadLogo) {
      setUploadSuccess("");
      if (!canEditLogo) setUploadError("Sem permissão para alterar o logo.");
      else if (!grupoId || !empresaIdResolved) setUploadError("Empresa não configurada.");
      else setUploadError("Sem permissão para alterar o logo desta empresa.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    if (!canUploadLogo) {
      if (!canEditLogo) setUploadError("Sem permissão para alterar o logo.");
      else if (!grupoId || !empresaIdResolved) setUploadError("Empresa não configurada.");
      else setUploadError("Sem permissão para alterar o logo desta empresa.");
      return;
    }
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setUploadError("Por favor, selecione uma imagem.");
      return;
    }

    if (!empresaIdResolved || !grupoId) {
      setUploadError("Empresa não configurada.");
      return;
    }

    setUploadError("");
    setUploadSuccess("");
    setUploading(true);

    try {
      const url = await uploadToStorage({
        grupoId,
        empresaId: empresaIdResolved,
        pasta: "empresa_logo",
        file,
        filenamePrefix: "logo",
      });

      await updateDoc(doc(db, "empresas", empresaIdResolved), {
        logoUrl: url,
        logoUpdatedAt: Date.now(),
      });

      setLogoUrl(url);
      setUploadSuccess("Logo atualizado!");

      if (recarregarEmpresa) {
        recarregarEmpresa();
      }

      setTimeout(() => setUploadSuccess(""), 3000);
    } catch (err) {
      console.error("Erro ao fazer upload:", err);
      setUploadError(err.message || "Erro ao enviar logo.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className={`kiosk-landing-root ${tema}`} data-testid="kiosk-landing">
      <div className="kiosk-landing-bg">
        <div className="kiosk-landing-orb kiosk-landing-orb-1"></div>
        <div className="kiosk-landing-orb kiosk-landing-orb-2"></div>
        <div className="kiosk-landing-grid"></div>
      </div>

      <div className="kiosk-landing-container">
        <div className="kiosk-landing-card">
          <div className="kiosk-landing-logo-area">
            {canEditLogo ? (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  data-testid="input-logo-upload"
                />

                <button
                  type="button"
                  className="kiosk-landing-logo-btn"
                  onClick={handleLogoClick}
                  disabled={uploading}
                  data-testid="button-upload-logo"
                >
                  {uploading ? (
                    <div className="kiosk-landing-uploading">
                      <div className="kiosk-landing-spinner"></div>
                      <span>Enviando...</span>
                    </div>
                  ) : logoUrl ? (
                    <div className="kiosk-landing-logo-wrapper">
                      <img
                        src={logoUrl}
                        alt="Logo da empresa"
                        className="kiosk-landing-logo"
                        data-testid="img-logo-landing"
                      />
                      <div className="kiosk-landing-logo-overlay">
                        <div className="kiosk-landing-logo-overlay-inner">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                          <span>Alterar</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="kiosk-landing-logo-placeholder">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="3" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <path d="M21 15l-5-5L5 21" />
                      </svg>
                      <span>Clique para adicionar logo</span>
                      <span className="kiosk-landing-logo-hint">PNG ou JPG recomendado</span>
                    </div>
                  )}
                </button>

                {uploadError && (
                  <div className="kiosk-landing-upload-error">{uploadError}</div>
                )}
                {uploadSuccess && (
                  <div className="kiosk-landing-upload-success">{uploadSuccess}</div>
                )}
              </>
            ) : (
              <div className="kiosk-landing-logo-static" data-testid="kiosk-logo-static">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Logo da empresa"
                    className="kiosk-landing-logo"
                    data-testid="img-logo-landing"
                  />
                ) : (
                  <div className="kiosk-landing-logo-placeholder">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="3" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                    <span>Logo não configurado</span>
                    <span className="kiosk-landing-logo-hint">Sem permissão para alterar logo</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <h1 className="kiosk-landing-title">{empresaNome}</h1>
          <p className="kiosk-landing-subtitle">Sistema de Manutenção</p>

          <div className="kiosk-landing-actions">
            <button
              type="button"
              className="kiosk-landing-btn kiosk-landing-btn-config"
              onClick={() => setShowThemeSelector(true)}
              data-testid="button-configurar"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              <span>Alterar Tema</span>
            </button>

            <button
              type="button"
              className="kiosk-landing-btn kiosk-landing-btn-primary"
              onClick={handleGoToAbrirOs}
              data-testid="button-abrir-os"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
              <span>Abrir Ordem de Serviço</span>
            </button>
          </div>

          <div className="kiosk-landing-theme-indicator">
            <span>Tema atual:</span>
            <span className="kiosk-landing-theme-name">
              {TEMAS.find((t) => t.id === tema)?.nome || tema}
            </span>
          </div>

          <button
            type="button"
            className="kiosk-landing-logout"
            onClick={handleLogout}
            data-testid="button-logout"
          >
            <LogoutIcon />
            <span>Sair</span>
          </button>
        </div>
      </div>

      {showThemeSelector && (
        <div className="kiosk-landing-modal-overlay" onClick={() => setShowThemeSelector(false)}>
          <div className="kiosk-landing-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Escolha o Tema</h2>
            <div className="kiosk-landing-theme-grid">
              {TEMAS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`kiosk-landing-theme-option ${tema === t.id ? "active" : ""} ${t.dark ? "dark" : "light"}`}
                  onClick={() => handleSelectTheme(t.id)}
                  data-testid={`button-theme-${t.id}`}
                >
                  <div className="kiosk-landing-theme-preview">
                    <div className="kiosk-theme-preview-bar"></div>
                    <div className="kiosk-theme-preview-content">
                      <div className="kiosk-theme-preview-card"></div>
                    </div>
                  </div>
                  <span>{t.nome}</span>
                  {tema === t.id && (
                    <svg className="kiosk-theme-check" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="kiosk-landing-modal-close"
              onClick={() => setShowThemeSelector(false)}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
